/**
 * MapStateManager
 *
 * Pure state container for CairnMap. Holds all reactive application state
 * with no dependencies on Lit, MapLibre, DOM APIs, or Capacitor.
 * Designed to be incrementally extracted from cairn-map.ts.
 */

import type { MapMode, DrawSubContext } from '../services/trip-feature-manager.js'
import type { TimelineState } from '../services/timeline/timeline-service.js'
import type { UnitSystem } from '../utils/units.js'
import type { CoordinateFormat, CoordinatePrecision } from '../lib/coordinates.js'
import type { SettingsStorage } from '../storage/settings-storage.js'
import type { ActivityType } from 'cairn-compass'
import type { Feature } from '../schemas/feature.js'

// ─── State shape ────────────────────────────────────────────────────────────

export interface MapState {
  // Loading
  isLoading: boolean

  // Location
  currentLocation:
    | { lat: number; lng: number; heading: number | undefined; accuracy: number | undefined }
    | undefined
  currentElevation: number | undefined
  currentVelocity: number | undefined
  currentActivity: ActivityType | undefined
  hasLocationData: boolean
  locationPermissionGranted: boolean
  isFollowingUser: boolean

  // Recording
  isRecording: boolean

  // Mode
  contextMode: 'location' | 'drawing' | 'editing' | 'extending'
  mapMode: MapMode
  drawSubContext: DrawSubContext
  selectedDrawingTool: string | null

  // UI panels
  showLayerSwitcher: boolean

  // Settings
  unitSystem: UnitSystem
  coordinateFormat: CoordinateFormat
  coordinatePrecision: CoordinatePrecision

  // Selection
  selectedFeatures: Feature[]

  // Measurement
  measureDistance: string | undefined
  measureBearing: string | undefined
  measureArea: string | undefined
  measurePerimeter: string | undefined
  measureType: 'none' | 'line' | 'shape' | 'point'
  measureLocation: string | undefined

  // Timeline
  timelineState: TimelineState

  // 3D
  is3dMode: boolean
}

// ─── Listener type ──────────────────────────────────────────────────────────

export type MapStateListener = (state: Readonly<MapState>, changedKeys: Set<keyof MapState>) => void

// ─── Measurement data helper ────────────────────────────────────────────────

export interface MeasureData {
  distance?: string | undefined
  bearing?: string | undefined
  area?: string | undefined
  perimeter?: string | undefined
  type: 'none' | 'line' | 'shape' | 'point'
  location?: string | undefined
}

// ─── Default state factory ───────────────────────────────────────────────────

function buildDefaultTimelineState(): TimelineState {
  const now = new Date()
  const floored = new Date(now)
  floored.setMinutes(0, 0, 0)
  const endTime = new Date(floored.getTime() + 18 * 60 * 60 * 1000)
  return {
    startTime: new Date(endTime.getTime() - 24 * 60 * 60 * 1000),
    endTime,
    currentTime: new Date(now),
    playbackDuration: 90,
    isPlaying: false,
    isLooping: false,
  }
}

function buildDefaultState(): MapState {
  return {
    isLoading: true,
    currentLocation: undefined,
    currentElevation: undefined,
    currentVelocity: undefined,
    currentActivity: undefined,
    hasLocationData: false,
    locationPermissionGranted: false,
    isFollowingUser: false,
    isRecording: false,
    contextMode: 'location',
    mapMode: 'map',
    drawSubContext: 'tools',
    selectedDrawingTool: null,
    showLayerSwitcher: false,
    unitSystem: 'imperial',
    coordinateFormat: 'DD',
    coordinatePrecision: {},
    selectedFeatures: [],
    measureDistance: undefined,
    measureBearing: undefined,
    measureArea: undefined,
    measurePerimeter: undefined,
    measureType: 'none',
    measureLocation: undefined,
    timelineState: buildDefaultTimelineState(),
    is3dMode: false,
  }
}

// ─── MapStateManager ─────────────────────────────────────────────────────────

export class MapStateManager {
  private _state: MapState = buildDefaultState()
  private _listeners = new Set<MapStateListener>()
  private _batchDepth = 0
  private _pendingChangedKeys = new Set<keyof MapState>()

  // ── Read ────────────────────────────────────────────────────────────────

  get state(): Readonly<MapState> {
    return this._state
  }

  // ── Listeners ────────────────────────────────────────────────────────────

  addListener(fn: MapStateListener): void {
    this._listeners.add(fn)
  }

  removeListener(fn: MapStateListener): void {
    this._listeners.delete(fn)
  }

  // ── Notification ─────────────────────────────────────────────────────────

  private notify(changedKeys: Set<keyof MapState>): void {
    if (this._batchDepth > 0) {
      for (const k of changedKeys) {
        this._pendingChangedKeys.add(k)
      }
      return
    }
    const snapshot = Object.freeze({ ...this._state })
    for (const fn of this._listeners) {
      fn(snapshot, changedKeys)
    }
  }

  // ── Batch ─────────────────────────────────────────────────────────────────

  batch(fn: () => void): void {
    this._batchDepth++
    try {
      fn()
    } finally {
      this._batchDepth--
      if (this._batchDepth === 0 && this._pendingChangedKeys.size > 0) {
        const keys = new Set(this._pendingChangedKeys)
        this._pendingChangedKeys.clear()
        const snapshot = Object.freeze({ ...this._state })
        for (const listener of this._listeners) {
          listener(snapshot, keys)
        }
      }
    }
  }

  // ── Generic private setter ────────────────────────────────────────────────

  private set<K extends keyof MapState>(key: K, value: MapState[K]): void {
    if (this._state[key] === value) return
    this._state = { ...this._state, [key]: value }
    this.notify(new Set([key]))
  }

  // ── Loading ───────────────────────────────────────────────────────────────

  setIsLoading(value: boolean): void {
    this.set('isLoading', value)
  }

  // ── Location ──────────────────────────────────────────────────────────────

  setCurrentLocation(
    value:
      | { lat: number; lng: number; heading: number | undefined; accuracy: number | undefined }
      | undefined
  ): void {
    this._state = { ...this._state, currentLocation: value }
    this.notify(new Set(['currentLocation']))
  }

  setCurrentElevation(value: number | undefined): void {
    this.set('currentElevation', value)
  }

  setCurrentVelocity(value: number | undefined): void {
    this.set('currentVelocity', value)
  }

  setCurrentActivity(value: ActivityType | undefined): void {
    this.set('currentActivity', value)
  }

  setHasLocationData(value: boolean): void {
    this.set('hasLocationData', value)
  }

  setLocationPermissionGranted(value: boolean): void {
    this.set('locationPermissionGranted', value)
  }

  setIsFollowingUser(value: boolean): void {
    this.set('isFollowingUser', value)
  }

  toggleFollowUser(): boolean {
    const next = !this._state.isFollowingUser
    this.set('isFollowingUser', next)
    return next
  }

  // ── Recording ─────────────────────────────────────────────────────────────

  setIsRecording(value: boolean): void {
    this.set('isRecording', value)
  }

  // ── Mode ──────────────────────────────────────────────────────────────────

  setContextMode(value: 'location' | 'drawing' | 'editing' | 'extending'): void {
    this.set('contextMode', value)
  }

  setMapMode(mode: MapMode): void {
    this.set('mapMode', mode)
  }

  setDrawSubContext(value: DrawSubContext): void {
    this.set('drawSubContext', value)
  }

  setSelectedDrawingTool(tool: string | null): void {
    this.set('selectedDrawingTool', tool)
  }

  // ── UI panels ─────────────────────────────────────────────────────────────

  toggleLayerSwitcher(): void {
    this.set('showLayerSwitcher', !this._state.showLayerSwitcher)
  }

  closeLayerSwitcher(): void {
    this.set('showLayerSwitcher', false)
  }

  // ── 3D ────────────────────────────────────────────────────────────────────

  toggle3dMode(): boolean {
    const next = !this._state.is3dMode
    this.set('is3dMode', next)
    return next
  }

  setIs3dMode(value: boolean): void {
    this.set('is3dMode', value)
  }

  // ── Settings ──────────────────────────────────────────────────────────────

  /**
   * Read unit system, coordinate format, and coordinate precision from a
   * SettingsStorage instance and apply them to state.  No DOM access occurs
   * here — the storage reads from localStorage, which is injected via the
   * parameter so tests can mock it freely.
   */
  loadSettings(storage: SettingsStorage): void {
    const settings = storage.getSettingsWithDefaults()
    this.batch(() => {
      this.set('unitSystem', settings.unitSystem as UnitSystem)
      this.set('coordinateFormat', settings.coordinateFormat as CoordinateFormat)
      if (settings.coordinatePrecision) {
        this._state = { ...this._state, coordinatePrecision: settings.coordinatePrecision }
      }
      this._pendingChangedKeys.add('coordinatePrecision')
    })
  }

  applySettings(
    partial: Partial<Pick<MapState, 'unitSystem' | 'coordinateFormat' | 'coordinatePrecision'>>
  ): void {
    this.batch(() => {
      if (partial.unitSystem !== undefined) {
        this.set('unitSystem', partial.unitSystem)
      }
      if (partial.coordinateFormat !== undefined) {
        this.set('coordinateFormat', partial.coordinateFormat)
      }
      if (partial.coordinatePrecision !== undefined) {
        this._state = { ...this._state, coordinatePrecision: partial.coordinatePrecision }
        this._pendingChangedKeys.add('coordinatePrecision')
      }
    })
  }

  // ── Selection ─────────────────────────────────────────────────────────────

  setSelectedFeatures(features: Feature[]): void {
    this._state = { ...this._state, selectedFeatures: features }
    this.notify(new Set(['selectedFeatures']))
  }

  clearSelectedFeatures(): void {
    if (this._state.selectedFeatures.length === 0) return
    this._state = { ...this._state, selectedFeatures: [] }
    this.notify(new Set(['selectedFeatures']))
  }

  removeFeatureFromSelection(id: string): void {
    const next = this._state.selectedFeatures.filter((f: Feature) => f.id !== id)
    if (next.length === this._state.selectedFeatures.length) return
    this._state = { ...this._state, selectedFeatures: next }
    this.notify(new Set(['selectedFeatures']))
  }

  // ── Measurement ───────────────────────────────────────────────────────────

  updateMeasurement(data: MeasureData): void {
    this._state = {
      ...this._state,
      measureDistance: data.distance,
      measureBearing: data.bearing,
      measureArea: data.area,
      measurePerimeter: data.perimeter,
      measureType: data.type,
      measureLocation: data.location,
    }
    this.notify(
      new Set<keyof MapState>([
        'measureDistance',
        'measureBearing',
        'measureArea',
        'measurePerimeter',
        'measureType',
        'measureLocation',
      ])
    )
  }

  clearMeasurement(): void {
    this._state = {
      ...this._state,
      measureDistance: undefined,
      measureBearing: undefined,
      measureArea: undefined,
      measurePerimeter: undefined,
      measureType: 'none',
      measureLocation: undefined,
    }
    this.notify(
      new Set<keyof MapState>([
        'measureDistance',
        'measureBearing',
        'measureArea',
        'measurePerimeter',
        'measureType',
        'measureLocation',
      ])
    )
  }

  // ── Timeline ──────────────────────────────────────────────────────────────

  setTimelineState(value: TimelineState): void {
    this._state = { ...this._state, timelineState: value }
    this.notify(new Set(['timelineState']))
  }

  // ── Lifecycle ─────────────────────────────────────────────────────────────

  dispose(): void {
    this._listeners.clear()
    this._pendingChangedKeys.clear()
  }
}
